import { useEffect, useState, useCallback } from 'react';
import { ClipboardList, Plus, X, Scan, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { api } from '@/utils/api';

import { type Product, type Audit, type VarianceItem, COUNT_LOCATION_LABELS, needsReason } from './stockAuditTypes';
import { NewStockAuditModal } from './NewStockAuditModal';

export function StockAuditPage({approvalOnly=false}:{approvalOnly?:boolean}={}) {
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showNewModal, setShowNewModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<'blind' | 'standard'>('blind');
  const [newLocation, setNewLocation] = useState<string>('FACTORY');
  const [reasonDrafts, setReasonDrafts] = useState<Record<string, string>>({});

  const [activeAudit, setActiveAudit] = useState<Audit | null>(null);
  const [scanInput, setScanInput] = useState('');
  const [countQty, setCountQty] = useState('');
  const [location, setLocation] = useState('');
  const [lookupProduct, setLookupProduct] = useState<Product | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const loadAudits = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/warehouse/stock-audit');
      if (!Array.isArray(data.data)) throw new Error('Invalid audit list');
      setAudits(approvalOnly ? data.data.filter((a:Audit)=>a.status==='completed'||a.status==='approved') : data.data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load audits');
    } finally {
      setLoading(false);
    }
  }, [approvalOnly]);

  useEffect(() => { loadAudits(); }, [loadAudits]);

  const createAudit = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post('/warehouse/stock-audit', { name: newName, type: newType, locationCode: newLocation });
      setShowNewModal(false);
      setNewName('');
      setActiveAudit(data.data);
      await loadAudits();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to create audit');
    }
  };

  const lookupBarcode = async () => {
    if (!scanInput.trim()) return;
    try {
      const { data } = await api.post('/inventory/lookup', { barcode: scanInput.trim() });
      setLookupProduct(data.data.product);
    } catch {
      alert('Barcode not found');
      setLookupProduct(null);
    }
  };

  const submitCount = async () => {
    if (!activeAudit || !lookupProduct || !countQty) return;
    setSubmitting(true);
    try {
      await api.post(`/warehouse/stock-audit/${activeAudit.auditId}/count`, {
        productId: lookupProduct.id,
        physicalCount: Number(countQty),
        location: location || null,
      });
      // Refresh audit
      const { data } = await api.get(`/warehouse/stock-audit/${activeAudit.auditId}`);
      setActiveAudit(data.data);
      setScanInput('');
      setCountQty('');
      setLookupProduct(null);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to submit count');
    } finally {
      setSubmitting(false);
    }
  };

  const completeAudit = async () => {
    if (!activeAudit) return;
    try {
      const { data } = await api.post(`/warehouse/stock-audit/${activeAudit.auditId}/complete`);
      setActiveAudit(data.data);
      await loadAudits();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to complete audit');
    }
  };

  const approveAudit = async () => {
    if (!activeAudit) return;
    try {
      const { data } = await api.post(`/warehouse/stock-audit/${activeAudit.auditId}/approve`);
      setActiveAudit(data.data);
      await loadAudits();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to approve audit');
    }
  };

  const saveReason = async (it: VarianceItem) => {
    if (!activeAudit) return;
    try {
      const { data } = await api.post(`/warehouse/stock-audit/${activeAudit.auditId}/reason`, {
        productId: it.productId,
        reason: reasonDrafts[it.productId] ?? it.reason ?? '',
      });
      setActiveAudit(data.data);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Failed to save reason');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="h-6 w-6 text-[var(--ink-0)]" />
            {approvalOnly ? 'Count Approvals' : 'Stock Audit'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">Blind counts with variance reports — physical vs system</p>
        </div>
        {!approvalOnly && <button
          onClick={() => setShowNewModal(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
        >
          <Plus className="h-4 w-4" /> New Audit
        </button>}
      </div>

      {error && <div role="alert" className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">{error}</div>}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
        {/* Audit list */}
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">All Audits</div>
          {loading ? (
            <div className="p-4 text-center text-sm text-gray-500"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
          ) : audits.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">No audits to display.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {audits.map((a) => (
                <button
                  key={a.auditId}
                  onClick={() => setActiveAudit(a)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 ${activeAudit?.auditId === a.auditId ? 'bg-[var(--saffron-50)]' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{a.auditId}</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs ${
                      a.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800'
                      : a.status === 'completed' ? 'bg-blue-100 text-blue-800'
                      : 'bg-green-100 text-green-800'
                    }`}>
                      {a.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-gray-600">{a.name}</div>
                  {a.locationCode && (
                    <div className="mt-1 text-xs text-gray-500">Location: {COUNT_LOCATION_LABELS[a.locationCode] ?? a.locationCode}</div>
                  )}
                  <div className="mt-1 text-xs text-gray-500">{a.counts?.length || 0} items counted</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Active audit panel */}
        <div className="rounded-lg border border-gray-200 bg-white">
          {!activeAudit ? (
            <div className="p-12 text-center text-sm text-gray-500">Select an audit to review its counts.</div>
          ) : (
            <div>
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{activeAudit.auditId} — {activeAudit.name}</h2>
                    <div className="mt-1 text-xs text-gray-500">
                      Started {new Date(activeAudit.createdAt).toLocaleString()}
                      {activeAudit.locationCode && <> · Location: {COUNT_LOCATION_LABELS[activeAudit.locationCode] ?? activeAudit.locationCode}</>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {!approvalOnly && activeAudit.status === 'in_progress' && activeAudit.counts.length > 0 && (
                      <button onClick={completeAudit} className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700">
                        Complete & Generate Variance
                      </button>
                    )}
                    {activeAudit.status === 'completed' && (
                      <button onClick={approveAudit} className="rounded-lg bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700">
                        Approve
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {!approvalOnly && activeAudit.status === 'in_progress' && (
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                  <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1">
                    <Scan className="h-3 w-3" /> Scan or type barcode
                  </div>
                  <div className="grid grid-cols-[1fr_auto] gap-2">
                    <input
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') lookupBarcode(); }}
                      placeholder="Scan barcode and press Enter…"
                      className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
                    />
                    <button onClick={lookupBarcode} className="rounded-lg bg-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">
                      Lookup
                    </button>
                  </div>
                  {lookupProduct && (
                    <div className="mt-3 rounded-lg border border-[var(--ink-3)] bg-white p-3">
                      <div className="mb-2 text-sm font-medium text-gray-900">{lookupProduct.name} <span className="text-xs text-gray-500">({lookupProduct.sku})</span></div>
                      <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                        <input
                          type="number"
                          value={countQty}
                          onChange={(e) => setCountQty(e.target.value)}
                          placeholder="Physical count"
                          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                        />
                        <input
                          type="text"
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                          placeholder="Location (optional)"
                          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none"
                        />
                        <button
                          disabled={!countQty || submitting}
                          onClick={submitCount}
                          className="rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
                        >
                          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Record'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Variance report */}
              {activeAudit.results ? (
                <div className="px-6 py-4">
                  <div className="mb-3 flex items-center gap-4 text-sm">
                    <span className="rounded bg-gray-100 px-2 py-1">Total: <b>{activeAudit.results.summary.totalCounted}</b></span>
                    <span className="rounded bg-green-100 px-2 py-1 text-green-800"><CheckCircle2 className="inline h-3 w-3" /> OK: <b>{activeAudit.results.summary.matched}</b></span>
                    <span className="rounded bg-red-100 px-2 py-1 text-red-800"><AlertTriangle className="inline h-3 w-3" /> Flagged: <b>{activeAudit.results.summary.flagged}</b></span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-3 py-2">Product</th>
                          <th className="px-3 py-2">System</th>
                          <th className="px-3 py-2">Physical</th>
                          <th className="px-3 py-2">Variance</th>
                          <th className="px-3 py-2">%</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Reason</th>
                          <th className="px-3 py-2">Approval</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {activeAudit.results.items.map((it) => (
                          <tr key={it.productId} className={it.status === 'flagged' ? 'bg-red-50' : ''}>
                            <td className="px-3 py-2">
                              {it.productName} <span className="text-xs text-gray-500">({it.productSku})</span>
                              {(it.missingSerials ?? []).map((code) => (
                                <div key={code} className="text-xs text-red-700">Serial …{code.slice(-5)} not found</div>
                              ))}
                              {(it.extraSerials ?? []).map((code) => (
                                <div key={code} className="text-xs text-blue-700">Serial …{code.slice(-5)} found here</div>
                              ))}
                            </td>
                            <td className="px-3 py-2">{it.systemQty}</td>
                            <td className="px-3 py-2">{it.physicalQty}</td>
                            <td className="px-3 py-2">{it.variance > 0 ? `+${it.variance}` : it.variance}</td>
                            <td className="px-3 py-2">{it.variancePercent}%</td>
                            <td className="px-3 py-2">
                              <span className={`rounded-full px-2 py-0.5 text-xs ${it.status === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {it.status}
                              </span>
                            </td>
                            <td className="px-3 py-2">
                              {!approvalOnly && needsReason(it) && activeAudit.status === 'completed' ? (
                                <div className="flex gap-1">
                                  <input
                                    aria-label={`Reason for ${it.productName}`}
                                    value={reasonDrafts[it.productId] ?? it.reason ?? ''}
                                    onChange={(e) => setReasonDrafts((d) => ({ ...d, [it.productId]: e.target.value }))}
                                    className="w-44 rounded-lg border border-gray-300 px-2 py-1 text-xs focus:border-[var(--ink-0)] focus:outline-none"
                                  />
                                  <button
                                    aria-label={`Save reason for ${it.productName}`}
                                    onClick={() => saveReason(it)}
                                    className="rounded-lg bg-gray-700 px-2 py-1 text-xs font-medium text-white hover:bg-gray-800"
                                  >
                                    Save
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-600">{it.reason || '—'}</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {!needsReason(it) ? '—' : activeAudit.status === 'approved' ? (
                                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">Approved</span>
                              ) : (
                                <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800">Waiting · QA Manager</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="px-6 py-4">
                  <div className="mb-2 text-sm font-semibold text-gray-700">Counts ({activeAudit.counts.length})</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-3 py-2">Product</th>
                          <th className="px-3 py-2">Qty</th>
                          <th className="px-3 py-2">Location</th>
                          <th className="px-3 py-2">At</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {activeAudit.counts.map((c, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2">{c.productName}</td>
                            <td className="px-3 py-2">{c.physicalCount}</td>
                            <td className="px-3 py-2">{c.location || '-'}</td>
                            <td className="px-3 py-2 text-xs text-gray-500">{new Date(c.countedAt).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!approvalOnly && showNewModal && <NewStockAuditModal {...{setShowNewModal,newName,setNewName,newType,setNewType,newLocation,setNewLocation,createAudit}}/>}
    </div>
  );
}
